import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPolicies, getSops, getForms, getReferenceMaterials } from '../../lib/policies';
import { StatTile } from '../../components/StatTile';
import ProfileTabBar from '../../components/ProfileTabBar';
import DataTable from '../../components/DataTable';

const STATUS_META = {
  current:      { label: 'Current',      bg: 'var(--success-bg)', color: 'var(--success)' },
  needs_review: { label: 'Needs Review', bg: 'var(--warn-bg)',    color: 'var(--warn)' },
  draft:        { label: 'Draft',        bg: 'var(--bg2)',        color: 'var(--text3)' },
};

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.draft;
  return <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 9px', borderRadius: 10, background: m.bg, color: m.color }}>{m.label}</span>;
};

const TagChips = ({ tags }) => (
  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
    {(tags || []).slice(0, 3).map(t => (
      <span key={t} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--bg2)', color: 'var(--text3)', border: '0.5px solid var(--border)' }}>{t}</span>
    ))}
    {(tags || []).length > 3 && <span style={{ fontSize: 10, color: 'var(--text3)' }}>+{tags.length - 3}</span>}
  </div>
);

export default function PoliciesProcedures() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('manual');
  const [policies, setPolicies] = useState([]);
  const [sops, setSops] = useState([]);
  const [forms, setForms] = useState([]);
  const [refs, setRefs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    const [p, s, f, r] = await Promise.all([getPolicies(), getSops(), getForms(), getReferenceMaterials()]);
    setPolicies(p.data || []);
    setSops(s.data || []);
    setForms(f.data || []);
    setRefs(r.data || []);
    setLoading(false);
  };

  const needsReview = policies.filter(p => p.status === 'needs_review').length;

  const policyColumns = [
    { accessorKey: 'section_number', header: 'Section', size: 90, meta: { mobile: 'primary', mono: true } },
    { accessorKey: 'title', header: 'Title', size: 260, meta: { mobile: 'primary' } },
    { id: 'status', header: 'Status', size: 110, meta: { mobile: 'secondary' }, cell: info => <StatusPill status={info.row.original.status} /> },
    { accessorKey: 'version', header: 'Version', size: 70, meta: { mobile: 'secondary' } },
    { accessorKey: 'review_due_date', header: 'Review Due', size: 110, meta: { mobile: 'secondary' } },
    { id: 'tags', header: 'Regulatory Tags', size: 200, enableSorting: false, meta: { mobile: 'hidden' }, cell: info => <TagChips tags={info.row.original.regulatory_tags} /> },
  ];

  const sopColumns = [
    { accessorKey: 'code', header: 'Code', size: 100, meta: { mobile: 'primary', mono: true } },
    { accessorKey: 'title', header: 'Title', size: 300, meta: { mobile: 'primary' } },
    { id: 'link', header: '', size: 90, enableSorting: false, meta: { mobile: 'hidden' }, cell: info => info.row.original.file_url && <a href={info.row.original.file_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 12, color: 'var(--accent)' }}>Open →</a> },
  ];

  const formColumns = [
    { accessorKey: 'title', header: 'Title', size: 260, meta: { mobile: 'primary' } },
    { accessorKey: 'category', header: 'Category', size: 120, meta: { mobile: 'secondary' } },
    { id: 'link', header: '', size: 90, enableSorting: false, meta: { mobile: 'hidden' }, cell: info => info.row.original.file_url && <a href={info.row.original.file_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 12, color: 'var(--accent)' }}>Open →</a> },
  ];

  const refColumns = [
    { accessorKey: 'title', header: 'Title', size: 300, meta: { mobile: 'primary' } },
    { accessorKey: 'source', header: 'Source', size: 140, meta: { mobile: 'secondary' } },
    { id: 'link', header: '', size: 90, enableSorting: false, meta: { mobile: 'hidden' }, cell: info => info.row.original.file_url && <a href={info.row.original.file_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 12, color: 'var(--accent)' }}>Open →</a> },
  ];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 8, marginBottom: 16 }}>
        <StatTile label="Total Policies" value={policies.length} />
        <StatTile label="SOPs & Job Aids" value={sops.length} />
        <StatTile label="Forms" value={forms.length} />
        <StatTile label="Needs Review" value={needsReview} tone={needsReview > 0 ? 'warn' : 'neutral'} />
      </div>

      <div className="card">
        <ProfileTabBar
          tabs={[
            { key: 'manual', label: 'Master Manual', badge: policies.length },
            { key: 'sops', label: 'SOPs & Job Aids', badge: sops.length },
            { key: 'forms', label: 'Forms Library', badge: forms.length },
            { key: 'reference', label: 'Reference Material', badge: refs.length },
          ]}
          activeKey={tab}
          onSelect={setTab}
        />

        {tab === 'manual' && (
          <DataTable
            tableId="policies-manual"
            columns={policyColumns}
            data={policies}
            getRowId={p => p.id}
            onRowClick={p => navigate(`/policies/${p.section_number}`)}
            loading={loading}
            emptyMessage="No policy sections yet."
          />
        )}
        {tab === 'sops' && (
          <DataTable tableId="policies-sops" columns={sopColumns} data={sops} getRowId={s => s.id} loading={loading} emptyMessage="No SOPs yet." />
        )}
        {tab === 'forms' && (
          <DataTable tableId="policies-forms" columns={formColumns} data={forms} getRowId={f => f.id} loading={loading} emptyMessage="No forms yet." />
        )}
        {tab === 'reference' && (
          <DataTable tableId="policies-reference" columns={refColumns} data={refs} getRowId={r => r.id} loading={loading} emptyMessage="No reference material yet." />
        )}
      </div>
    </div>
  );
}
